import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    count: { type: Number, default: 80 },
    linkDistance: { type: Number, default: 150 },
    mouseRadius: { type: Number, default: 200 },
    color: { type: String, default: "0, 0, 150" }
  }

  connect() {
    this.canvas = document.createElement("canvas")
    this.canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;"
    this.element.style.position = "relative"
    this.element.insertBefore(this.canvas, this.element.firstChild)

    this.ctx = this.canvas.getContext("2d")
    this.nodes = []
    this.time = 0
    this.mouse = { x: -9999, y: -9999 }
    this.resizeCanvas()

    this._onResize = this.resizeCanvas.bind(this)
    this._onMouseMove = this.onMouseMove.bind(this)
    this._onMouseLeave = this.onMouseLeave.bind(this)
    window.addEventListener("resize", this._onResize)
    this.element.style.pointerEvents = "auto"
    this.element.addEventListener("mousemove", this._onMouseMove)
    this.element.addEventListener("mouseleave", this._onMouseLeave)
    this.animate()
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    this.element.removeEventListener("mousemove", this._onMouseMove)
    this.element.removeEventListener("mouseleave", this._onMouseLeave)
    cancelAnimationFrame(this._raf)
    this.canvas.remove()
  }

  onMouseMove(e) {
    const rect = this.element.getBoundingClientRect()
    this.mouse.x = e.clientX - rect.left
    this.mouse.y = e.clientY - rect.top
  }

  onMouseLeave() {
    this.mouse.x = -9999
    this.mouse.y = -9999
  }

  resizeCanvas() {
    const rect = this.element.getBoundingClientRect()
    this.canvas.width = rect.width * devicePixelRatio
    this.canvas.height = rect.height * devicePixelRatio
    this.ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)
    this.width = rect.width
    this.height = rect.height
    this.scatterNodes()
  }

  scatterNodes() {
    this.nodes = []
    for (let i = 0; i < this.countValue; i++) {
      this.nodes.push({
        baseX: Math.random() * this.width,
        baseY: Math.random() * this.height,
        x: 0,
        y: 0,
        phase: Math.random() * Math.PI * 2,
        phaseY: Math.random() * Math.PI * 2,
        speed: 0.15 + Math.random() * 0.35,
        amplitude: 5 + Math.random() * 15,
        radius: 1.5 + Math.random() * 1.5
      })
    }
  }

  animate() {
    this.time += 0.016
    this.ctx.clearRect(0, 0, this.width, this.height)

    const rgb = this.colorValue
    const maxDist = this.linkDistanceValue
    const mouseR = this.mouseRadiusValue
    const mx = this.mouse.x
    const my = this.mouse.y

    // Update positions
    for (const node of this.nodes) {
      node.x = node.baseX + Math.sin(this.time * node.speed + node.phase) * node.amplitude
      node.y = node.baseY + Math.cos(this.time * node.speed * 0.7 + node.phaseY) * node.amplitude

      // Push away from mouse
      const dx = node.x - mx
      const dy = node.y - my
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < mouseR && dist > 0) {
        const force = (1 - dist / mouseR) * 30
        node.x += (dx / dist) * force
        node.y += (dy / dist) * force
      }
    }

    // Draw connections between nearby nodes
    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const a = this.nodes[i]
        const b = this.nodes[j]
        const dx = a.x - b.x
        const dy = a.y - b.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < maxDist) {
          const proximity = 1 - dist / maxDist
          let opacity = proximity * 0.15

          // Brighten lines near mouse
          const midX = (a.x + b.x) / 2
          const midY = (a.y + b.y) / 2
          const mouseDist = Math.sqrt((midX - mx) ** 2 + (midY - my) ** 2)
          if (mouseDist < mouseR) {
            opacity += (1 - mouseDist / mouseR) * 0.25
          }

          this.ctx.strokeStyle = `rgba(${rgb}, ${opacity})`
          this.ctx.lineWidth = proximity * 1.5
          this.ctx.beginPath()
          this.ctx.moveTo(a.x, a.y)
          this.ctx.lineTo(b.x, b.y)
          this.ctx.stroke()
        }
      }
    }

    // Draw lines from mouse to nearby nodes
    for (const node of this.nodes) {
      const dx = node.x - mx
      const dy = node.y - my
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < mouseR) {
        const opacity = (1 - dist / mouseR) * 0.3
        this.ctx.strokeStyle = `rgba(${rgb}, ${opacity})`
        this.ctx.lineWidth = (1 - dist / mouseR) * 1.5
        this.ctx.beginPath()
        this.ctx.moveTo(mx, my)
        this.ctx.lineTo(node.x, node.y)
        this.ctx.stroke()
      }
    }

    // Draw nodes
    for (const node of this.nodes) {
      let opacity = 0.1 + Math.sin(this.time * node.speed + node.phase) * 0.05
      let r = node.radius

      // Enlarge and brighten near mouse
      const dx = node.x - mx
      const dy = node.y - my
      const mouseDist = Math.sqrt(dx * dx + dy * dy)
      if (mouseDist < mouseR) {
        const closeness = 1 - mouseDist / mouseR
        opacity += closeness * 0.4
        r += closeness * 2
      }

      this.ctx.beginPath()
      this.ctx.arc(node.x, node.y, r, 0, Math.PI * 2)
      this.ctx.fillStyle = `rgba(${rgb}, ${opacity})`
      this.ctx.fill()
    }

    this._raf = requestAnimationFrame(() => this.animate())
  }
}
